import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Avatar,
  Button,
  ChecklistField,
  IconButton,
  NakedButton,
  NakedUl,
  SearchField,
  SkeletonLoader,
  Text,
  Truncated,
  useFormField,
  useTooltip,
} from '@frontend/design-system';
import { getSelectableUserDisplayName, SelectableUser } from '../../utils/user-utils';

// ── Types ─────────────────────────────────────────────────────────────────────

export type LocationGroup = {
  groupName: string;
  locations: { id: string; name: string }[];
};

export type LocationInfo = { name: string; groupName: string };

// ── Shared styles ─────────────────────────────────────────────────────────────

const cardStyle = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  width: 100%;
  background: ${theme.colors.neutral5};
  border: 1px solid ${theme.colors.neutral20};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(2)};
  box-sizing: border-box;
`;

const truncateStyle = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ── EmptyStateCard ────────────────────────────────────────────────────────────

type EmptyStateCardProps = { message: string };

export function EmptyStateCard({ message }: EmptyStateCardProps) {
  return (
    <div
      css={[
        cardStyle,
        css`
          height: 64px;
        `,
      ]}
    >
      <Icon
        name='help-small'
        color='subdued'
        css={css`
          flex-shrink: 0;
        `}
      />
      <Text color='subdued' size='medium'>
        {message}
      </Text>
    </div>
  );
}

// ── UserCard ──────────────────────────────────────────────────────────────────

type UserCardProps = {
  user: SelectableUser;
  onRemove: (userId: string) => void;
  isLastUser: boolean;
};

export function UserCard({ user, onRemove, isLastUser }: UserCardProps) {
  const { t } = useTranslation('settings');
  const fullName = getSelectableUserDisplayName(user) || t('Unknown User');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });

  return (
    <div
      css={[
        cardStyle,
        css`
          height: 64px;
        `,
      ]}
    >
      <Avatar name={fullName} size='small' />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 0;
        `}
      >
        <Text weight='bold' size='medium' css={truncateStyle}>
          {fullName}
        </Text>
        <Text size='small' color='subdued' css={truncateStyle}>
          {user.username}
        </Text>
      </div>
      <div {...triggerProps}>
        <IconButton
          label={isLastUser ? t('A workspace must have at least one user') : t('Remove user')}
          showLabelOnHover={!isLastUser}
          onClick={() => onRemove(user.id)}
          disabled={isLastUser}
          css={
            isLastUser
              ? css`
                  opacity: 0.35;
                  cursor: not-allowed;
                `
              : css`
                  cursor: pointer;
                `
          }
        >
          <Icon name='remove2' color='subdued' />
        </IconButton>
      </div>
      {isLastUser && <Tooltip {...tooltipProps}>{t('A workspace must have at least one user')}</Tooltip>}
    </div>
  );
}

// ── LocationCard ──────────────────────────────────────────────────────────────

type LocationCardProps = {
  locationId: string;
  locationName: string;
  groupName?: string;
  onRemove: (locationId: string) => void;
  isLastLocation: boolean;
};

export function LocationCard({ locationId, locationName, groupName, onRemove, isLastLocation }: LocationCardProps) {
  const { t } = useTranslation('settings');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'top' });

  return (
    <div
      css={[
        cardStyle,
        css`
          min-height: 64px;
          padding: ${theme.spacing(1.5, 2)};
        `,
      ]}
    >
      <Icon
        name='location-small'
        color='subdued'
        css={css`
          flex-shrink: 0;
        `}
      />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 0;
        `}
      >
        <Truncated as={Text} size='medium' tooltipOptions={{ placement: 'top' }}>
          {locationName}
        </Truncated>
        {groupName && (
          <Truncated as={Text} size='small' color='subdued' tooltipOptions={{ placement: 'top' }}>
            {groupName}
          </Truncated>
        )}
      </div>
      <div {...triggerProps}>
        <IconButton
          label={isLastLocation ? t('A workspace must have at least one location') : t('Remove location')}
          showLabelOnHover={!isLastLocation}
          onClick={() => onRemove(locationId)}
          disabled={isLastLocation}
          css={
            isLastLocation
              ? css`
                  opacity: 0.35;
                  cursor: not-allowed;
                `
              : css`
                  cursor: pointer;
                `
          }
        >
          <Icon name='remove2' color='subdued' />
        </IconButton>
      </div>
      {isLastLocation && <Tooltip {...tooltipProps}>{t('A workspace must have at least one location')}</Tooltip>}
    </div>
  );
}

// ── LocationSelector ──────────────────────────────────────────────────────────

type LocationSelectorProps = {
  checklistField: ReturnType<typeof useFormField<'checklist'>>;
  locationsByGroup: Record<string, LocationGroup>;
  /** Map of location ID → { name, groupName } for displaying assigned locations */
  allLocationsById: Map<string, LocationInfo>;
  allowRemoveAll?: boolean;
  onRemoveLocation?: (locationId: string) => void;
  /** Whether to show the "Add Locations to Workspace" title and description. Default: true */
  showTitle?: boolean;
};

export function LocationSelector({
  checklistField,
  locationsByGroup,
  allLocationsById,
  allowRemoveAll = true,
  onRemoveLocation,
  showTitle = true,
}: LocationSelectorProps) {
  const { t } = useTranslation('settings');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchField = useFormField({ type: 'text', value: '' });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Refs to avoid stale closures in the click-outside handler without re-registering the listener
  const pendingField = useFormField({ type: 'checklist', value: [] as string[] });
  const pendingValueRef = useRef<string[]>([]);
  const checklistOnChangeRef = useRef(checklistField.onChange);
  const searchFieldOnChangeRef = useRef(searchField.onChange);

  // Keep refs updated synchronously on every render
  pendingValueRef.current = pendingField.value;
  checklistOnChangeRef.current = checklistField.onChange;
  searchFieldOnChangeRef.current = searchField.onChange;

  // Reset pending + search when dropdown opens; no sync with already-assigned (they're excluded from list)
  useEffect(() => {
    if (isDropdownOpen) {
      pendingField.onChange({ name: 'pending-locations', value: [] });
      searchFieldOnChangeRef.current({ name: 'location-search', value: '' });
    }
  }, [isDropdownOpen]);

  // Click-outside: auto-assign whatever is pending, then close
  useEffect(() => {
    if (!isDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (!e.target) return;
      const target = e.target as Node;
      if (dropdownRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      // Auto-assign on click-outside using refs (avoids stale closure without re-registering)
      const pending = pendingValueRef.current;
      if (pending.length > 0) {
        checklistOnChangeRef.current({ name: 'workspace-locations', value: pending });
      }
      setIsDropdownOpen(false);
      searchFieldOnChangeRef.current({ name: 'location-search', value: '' });
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  const groups = useMemo(
    () =>
      Object.entries(locationsByGroup)
        .map(([groupId, group]) => ({ groupId, groupName: group.groupName, locations: group.locations }))
        .filter((g) => g.locations.length > 0),
    [locationsByGroup]
  );

  // Only show locations that are NOT yet assigned, further filtered by search query.
  // Groups with zero remaining locations are hidden entirely → no flash, no duplicates.
  const filteredGroups = useMemo(() => {
    const assignedSet = new Set(checklistField.value);
    const q = searchField.value.toLowerCase().trim();

    return groups
      .map(({ groupId, groupName, locations }) => {
        let available = locations.filter((l) => !assignedSet.has(l.id));
        if (q) {
          available = available.filter((l) => l.name.toLowerCase().includes(q) || groupName.toLowerCase().includes(q));
        }
        return { groupId, groupName, locations: available };
      })
      .filter((g) => g.locations.length > 0);
  }, [groups, checklistField.value, searchField.value]);

  const pendingSet = useMemo(() => new Set(pendingField.value), [pendingField.value]);

  // True when every available location has already been assigned → disable the trigger
  const allLocationsAssigned = useMemo(() => {
    if (groups.length === 0) return false;
    const assignedSet = new Set(checklistField.value);
    return groups.every(({ locations }) => locations.every((l) => assignedSet.has(l.id)));
  }, [groups, checklistField.value]);

  const {
    Tooltip: SearchTooltip,
    tooltipProps: searchTooltipProps,
    triggerProps: searchTriggerProps,
  } = useTooltip({
    placement: 'top',
  });

  // Close the dropdown automatically if all locations become assigned while it is open
  useEffect(() => {
    if (allLocationsAssigned && isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  }, [allLocationsAssigned, isDropdownOpen]);

  // Select All / Deselect All for a single group (operates on pendingField)
  const handleSelectGroup = useCallback(
    (groupLocationIds: string[]) => {
      const allSelected = groupLocationIds.every((id) => pendingSet.has(id));
      const value = allSelected
        ? groupLocationIds.filter((id) => pendingSet.has(id)) // all checked → uncheck all
        : groupLocationIds.filter((id) => !pendingSet.has(id)); // some/none checked → check missing
      pendingField.onChange({ name: 'pending-locations', value });
    },
    [pendingSet, pendingField]
  );

  // Assign button: add pending selections to checklist and close
  const handleAssign = useCallback(() => {
    const pending = pendingField.value;
    if (pending.length > 0) {
      checklistField.onChange({ name: 'workspace-locations', value: pending });
    }
    setIsDropdownOpen(false);
    searchFieldOnChangeRef.current({ name: 'location-search', value: '' });
  }, [checklistField, pendingField.value]);

  const handleRemoveLocation = useCallback(
    (locationId: string) => {
      if (onRemoveLocation) {
        onRemoveLocation(locationId);
      } else {
        checklistField.onChange({ name: 'workspace-locations', value: [locationId] });
      }
    },
    [checklistField, onRemoveLocation]
  );

  const selectedLocationIds = checklistField.value;
  const isLastLocation = !allowRemoveAll && selectedLocationIds.length <= 1;

  // Compute the animated height of the assigned-locations list.
  // Caps at 4 cards tall (64px × 4 + 8px gaps × 3 = 280px); extra items scroll.
  const LOCATION_CARD_HEIGHT_PX = 66;
  const LOCATION_GAP_PX = 8; // theme.spacing(1)
  const MAX_VISIBLE_LOCATION_CARDS = 4;
  const locationListHeight =
    selectedLocationIds.length === 0
      ? LOCATION_CARD_HEIGHT_PX // empty-state card
      : Math.min(selectedLocationIds.length, MAX_VISIBLE_LOCATION_CARDS) * LOCATION_CARD_HEIGHT_PX +
        (Math.min(selectedLocationIds.length, MAX_VISIBLE_LOCATION_CARDS) - 1) * LOCATION_GAP_PX;

  return (
    <>
      {/* Title + description — hidden in edit modal which already has its own header */}
      {showTitle && (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
          `}
        >
          <Text
            as='h3'
            weight='bold'
            css={css`
              font-size: 20px;
              margin: 0;
            `}
          >
            {t('Add Locations to Workspace')}
          </Text>
          <Text color='subdued' size='medium'>
            {t('Workspaces may be enabled or disabled based on assigned users access to the locations added.')}
          </Text>
        </div>
      )}

      {/* Search field trigger — opens dropdown on focus, filters locations while typing */}
      <div
        css={css`
          position: relative;
          width: 100%;
          z-index: ${isDropdownOpen ? theme.zIndex.dropdown : 1};
        `}
      >
        <div {...(allLocationsAssigned ? searchTriggerProps : {})}>
          <SearchField
            ref={triggerRef}
            {...searchField}
            name='location-search'
            placeholder={t('Select Locations')}
            onFocus={() => !allLocationsAssigned && setIsDropdownOpen(true)}
            disabled={allLocationsAssigned}
          />
        </div>
        {allLocationsAssigned && (
          <SearchTooltip {...searchTooltipProps}>{t('All locations have been assigned')}</SearchTooltip>
        )}

        {isDropdownOpen && (
          <div
            ref={dropdownRef}
            css={css`
              position: absolute;
              top: calc(100% + ${theme.spacing(0.5)});
              left: 0;
              right: 0;
              z-index: 20;
              background: ${theme.colors.white};
              border: 1px solid ${theme.colors.neutral20};
              border-radius: ${theme.borderRadius.medium};
              display: flex;
              flex-direction: column;
              box-shadow: ${theme.shadows.light};
              padding-top: ${theme.spacing(1)};
            `}
          >
            {/* Scrollable checklist area */}
            <div
              css={css`
                max-height: 260px;
                overflow-y: auto;
                padding: ${theme.spacing(1.5, 2, 0.75)};
                display: flex;
                flex-direction: column;
                gap: ${theme.spacing(1)};
              `}
            >
              {filteredGroups.length === 0 ? (
                <Text
                  color='subdued'
                  size='medium'
                  css={css`
                    padding: ${theme.spacing(1)} 0;
                  `}
                >
                  {t('No locations match your search')}
                </Text>
              ) : (
                <ChecklistField {...pendingField} name='pending-locations' label={t('Locations')} showLabel={false}>
                  {filteredGroups.map(({ groupId, groupName, locations }) => {
                    const groupLocationIds = locations.map((l) => l.id);
                    const allGroupSelected = groupLocationIds.every((id) => pendingSet.has(id));

                    return (
                      <Fragment key={groupId}>
                        <div
                          css={css`
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            gap: ${theme.spacing(1)};
                            margin-bottom: ${theme.spacing(1.5)};
                          `}
                        >
                          <Truncated
                            as={Text}
                            weight='bold'
                            size='medium'
                            tooltipOptions={{ placement: 'top' }}
                            css={css`
                              color: ${theme.colors.neutral90};
                              flex: 1;
                              min-width: 0;
                            `}
                          >
                            {groupName}
                          </Truncated>
                          <Button
                            variant='tertiary'
                            size='small'
                            onClick={() => handleSelectGroup(groupLocationIds)}
                            css={css`
                              padding: 0;
                              min-width: auto;
                              height: auto;
                              color: ${theme.colors.primary50};
                            `}
                          >
                            {allGroupSelected ? t('Deselect All') : t('Select All')}
                          </Button>
                        </div>

                        {locations.map((location) => (
                          <ChecklistField.Option key={location.id} name={location.id}>
                            <Truncated
                              as={Text}
                              tooltipOptions={{ placement: 'right' }}
                              css={css`
                                font-size: 14px;
                                max-width: 220px;
                              `}
                            >
                              {location.name}
                            </Truncated>
                          </ChecklistField.Option>
                        ))}
                      </Fragment>
                    );
                  })}
                </ChecklistField>
              )}
            </div>

            {/* Assign button — fixed at bottom, always visible */}
            <div
              css={css`
                padding: ${theme.spacing(1, 2)};
                display: flex;
                justify-content: flex-end;
              `}
            >
              <Button variant='primary' size='small' onClick={handleAssign} disabled={pendingField.value.length === 0}>
                {t('Assign')}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Assigned Locations — max-height of 4 cards (280px); shrinks to fit-content when fewer items */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(1)};
        `}
      >
        <Text weight='bold' size='medium'>
          {t('Assigned Locations')}
        </Text>
        <motion.div
          initial={{ height: locationListHeight }}
          animate={{ height: locationListHeight }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          css={css`
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(1)};
            width: 100%;
            overflow-y: auto;
          `}
        >
          <AnimatePresence initial={false}>
            {selectedLocationIds.length === 0 ? (
              <motion.div
                key='__empty__'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <EmptyStateCard message={t('No locations assigned')} />
              </motion.div>
            ) : (
              selectedLocationIds.map((locationId) => {
                const info = allLocationsById.get(locationId);
                return (
                  <motion.div
                    key={locationId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <LocationCard
                      locationId={locationId}
                      locationName={info?.name ?? locationId}
                      groupName={info?.groupName}
                      onRemove={handleRemoveLocation}
                      isLastLocation={isLastLocation}
                    />
                  </motion.div>
                );
              })
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
}

// ── AssignedUserList ──────────────────────────────────────────────────────────
// Animated list of assigned users. Caps visible rows at `maxVisible` cards and
// scrolls the rest. Uses opacity-only per-item transitions so flex-gap artifacts
// don't occur; the outer container animates height for smooth grow/shrink.

type AssignedUserListProps = {
  selectedUsers: SelectableUser[];
  onRemove: (userId: string) => void;
  /** Whether the last remaining user's remove button should be disabled. */
  isLastUser?: boolean;
  /** Max number of cards visible before the list scrolls. Defaults to 3. */
  maxVisible?: number;
};

const USER_CARD_HEIGHT_PX = 64;
const USER_GAP_PX = 8; // theme.spacing(1)

export function AssignedUserList({
  selectedUsers,
  onRemove,
  isLastUser = false,
  maxVisible = 3,
}: AssignedUserListProps) {
  const { t } = useTranslation('settings');

  const visibleCount = Math.min(selectedUsers.length || 1, maxVisible);
  const listHeight =
    selectedUsers.length === 0
      ? USER_CARD_HEIGHT_PX
      : visibleCount * USER_CARD_HEIGHT_PX + (visibleCount - 1) * USER_GAP_PX;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(2)};
      `}
    >
      <Text weight='bold' size='medium'>
        {t('Assigned Users')}
      </Text>
      <motion.div
        initial={{ height: listHeight }}
        animate={{ height: listHeight }}
        transition={{ duration: 0.25, ease: 'easeInOut' }}
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(1)};
          width: 100%;
          overflow-y: auto;
        `}
      >
        <AnimatePresence initial={false}>
          {selectedUsers.length === 0 ? (
            <motion.div
              key='__empty__'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <EmptyStateCard message={t('No users assigned')} />
            </motion.div>
          ) : (
            selectedUsers.map((user) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <UserCard user={user} onRemove={onRemove} isLastUser={isLastUser} />
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

// ── UserSearchField ───────────────────────────────────────────────────────────
// API-driven search field with an accumulating results dropdown.
// Results come from the server; previously-found users persist across searches
// in a separate "Previous results" section.

// Static styles — defined outside the component so Emotion doesn't recompute
// the hash on every render.
const userSearchStyles = {
  wrapper: css`
    position: relative;
    width: 100%;
  `,
  dropdown: css`
    position: absolute;
    top: calc(100% + ${theme.spacing(0.5)});
    left: 0;
    right: 0;
    z-index: 20;
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.medium};
    box-shadow: ${theme.shadows.light};
    max-height: 240px;
    overflow-y: auto;
  `,
  skeletonRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1, 2)};
  `,
  skeletonText: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    flex: 1;
    min-width: 0;
  `,
  resultItem: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};
    padding: ${theme.spacing(1, 2)};
    width: 100%;
    text-align: left;
    &:hover {
      background: ${theme.colors.neutral5};
    }
  `,
  resultText: css`
    display: flex;
    flex-direction: column;
    min-width: 0;
  `,
  sectionDivider: css`
    height: 1px;
    background: ${theme.colors.neutral20};
    margin: ${theme.spacing(0.5)} 0;
  `,
  sectionLabel: css`
    padding: ${theme.spacing(0.5, 2)};
  `,
  emptyState: css`
    padding: ${theme.spacing(1.5, 2)};
  `,
};

// ── UserSearchSkeleton ────────────────────────────────────────────────────────
// Three avatar + two-line text placeholder rows shown while the API is in-flight.
function UserSearchSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} css={userSearchStyles.skeletonRow}>
          <SkeletonLoader shape='circle' width={32} height={32} />
          <div css={userSearchStyles.skeletonText}>
            <SkeletonLoader height={14} width='55%' />
            <SkeletonLoader height={12} width='35%' />
          </div>
        </div>
      ))}
    </>
  );
}

export type UserSearchFieldProps = {
  /** Current raw search value (from useWorkspaceUserSearch). */
  searchKey: string;
  /** Called on every keystroke – the hook debounces before firing the API. */
  onSearchChange: (value: string) => void;
  /** Users from the latest completed API response, in API order, minus assigned users. */
  currentResults: SelectableUser[];
  /** Users from past searches not in currentResults, sorted A-Z, minus assigned users. */
  previousResults: SelectableUser[];
  /** True while the debounced API query is in-flight. */
  isSearching: boolean;
  /** True once the user has typed and the debounced search has fired at least once. */
  hasSearched: boolean;
  /**
   * Users from the latest API response that are all already assigned.
   * Non-empty only when the API returned valid users but every one is already assigned.
   * Used to show a contextual message like "Sammy MP is already assigned to this workspace".
   */
  alreadyAssignedUsers: SelectableUser[];
  /** Called when the user clicks a result in the dropdown. */
  onSelectUser: (user: SelectableUser) => void;
};

export function UserSearchField({
  searchKey,
  onSearchChange,
  currentResults,
  previousResults,
  isSearching,
  hasSearched,
  alreadyAssignedUsers,
  onSelectUser,
}: UserSearchFieldProps) {
  const { t } = useTranslation('settings');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // useFormField provides all required props for SearchField (id, onBlur, error, aria-invalid, etc.)
  const localField = useFormField({ type: 'text', value: '' });

  // Sync external resets (e.g. hook.reset() or modal close clears searchKey)
  useEffect(() => {
    if (searchKey === '' && localField.value !== '') {
      localField.onChange({ name: 'user-search', value: '' });
    }
    // Only react to searchKey flipping to empty — localField is the source of truth while typing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKey]);

  // Click-outside closes the dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleSelect = (user: SelectableUser) => {
    onSelectUser(user);
    localField.onChange({ name: 'user-search', value: '' });
    onSearchChange('');
    setIsOpen(false);
    // Return focus to the page so the dropdown doesn't immediately reopen
    wrapperRef.current?.querySelector('input')?.blur();
  };

  // Show the dropdown when:
  // - actively searching or has searched (skeleton / results / empty-state in Section 1)
  // - OR there are previous results to browse even with an empty field
  const showDropdown = isOpen && (isSearching || hasSearched || previousResults.length > 0);

  // Divider between sections only appears when Section 1 has visible content
  const showSectionDivider = isSearching || hasSearched;

  // Contextual message when all API results are already assigned — includes the user's name.
  const alreadyAssignedMessage = alreadyAssignedUsers.length
    ? (() => {
        const name = getSelectableUserDisplayName(alreadyAssignedUsers[0]);
        const extra = alreadyAssignedUsers.length - 1;
        return extra > 0
          ? t('{{name}} and {{count}} others are already assigned to this workspace', { name, count: extra })
          : t('{{name}} is already assigned to this workspace', { name });
      })()
    : '';

  return (
    <div ref={wrapperRef} css={userSearchStyles.wrapper}>
      <SearchField
        {...localField}
        name='user-search'
        // fieldComponentProps attaches directly to the inner <input> element,
        // giving us a native ChangeEvent where e.target.value is always correct.
        // This avoids the FieldChangeEvent union ambiguity in FieldLayout.
        fieldComponentProps={{
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            localField.onChange({ name: 'user-search', value });
            onSearchChange(value);
            if (!isOpen) setIsOpen(true);
          },
        }}
        onFocus={() => setIsOpen(true)}
        onClear={() => {
          localField.onChange({ name: 'user-search', value: '' });
          onSearchChange('');
        }}
        clearable
        placeholder={t('Search users...')}
      />

      {showDropdown && (
        <div ref={dropdownRef} css={userSearchStyles.dropdown}>
          {isSearching ? (
            <UserSearchSkeleton />
          ) : (
            <>
              {/* ── Section 1: Current results (API order, no label) ─────────── */}
              {currentResults.length > 0 ? (
                <NakedUl>
                  {currentResults.map((user) => {
                    const displayName = getSelectableUserDisplayName(user);
                    return (
                      <li key={user.id}>
                        <NakedButton
                          css={userSearchStyles.resultItem}
                          onMouseDown={(e) => {
                            // Prevent input blur before the selection fires
                            e.preventDefault();
                            handleSelect(user);
                          }}
                        >
                          <Avatar name={displayName} size='small' />
                          <div css={userSearchStyles.resultText}>
                            <Text weight='bold' size='medium' css={truncateStyle}>
                              {displayName}
                            </Text>
                            <Text size='small' color='subdued' css={truncateStyle}>
                              {user.username}
                            </Text>
                          </div>
                        </NakedButton>
                      </li>
                    );
                  })}
                </NakedUl>
              ) : hasSearched ? (
                // API had results but all already assigned → contextual name message
                // API had no valid results → "No results found"
                <div css={userSearchStyles.emptyState}>
                  <Text color='subdued' size='medium'>
                    {alreadyAssignedUsers.length > 0 ? alreadyAssignedMessage : t('No results found')}
                  </Text>
                </div>
              ) : null}

              {/* ── Section 2: Previous results (hidden when empty) ──────────── */}
              {previousResults.length > 0 && (
                <>
                  {/* Divider only renders when Section 1 has visible content above */}
                  {showSectionDivider && <div css={userSearchStyles.sectionDivider} />}
                  <div css={userSearchStyles.sectionLabel}>
                    <Text size='small' color='subdued'>
                      {t('Previous results')}
                    </Text>
                  </div>
                  <NakedUl>
                    {previousResults.map((user) => {
                      const displayName = getSelectableUserDisplayName(user);
                      return (
                        <li key={user.id}>
                          <NakedButton
                            css={userSearchStyles.resultItem}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelect(user);
                            }}
                          >
                            <Avatar name={displayName} size='small' />
                            <div css={userSearchStyles.resultText}>
                              <Text weight='bold' size='medium' css={truncateStyle}>
                                {displayName}
                              </Text>
                              <Text size='small' color='subdued' css={truncateStyle}>
                                {user.username}
                              </Text>
                            </div>
                          </NakedButton>
                        </li>
                      );
                    })}
                  </NakedUl>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
